import { useState } from 'react';
import toast from 'react-hot-toast';
import { CountrySelect } from '../common/CountrySelect';
import { BrandImageUpload } from '../common/BrandImageUpload';
import { TagInput } from '../CuratedWatches/TagInput';
import { LookupFieldSelect } from '../CuratedWatches/LookupFieldSelect';
import { LookupMultiSelect } from '../CuratedWatches/LookupMultiSelect';
import { RequestNewValueModal } from './RequestNewValueModal';
import {
  updateBrandFromPortal,
  type BrandPortalEditableFields,
} from '../../services/firebase';
import type { WatchBrand } from '../../types';
import { brandShowsProductLinks } from '../../services/productLinks';

/**
 * EditMyBrandPanel — Brand Portal Phase 2.2b.
 *
 * A focused brand-owner editing form, organised into sections, covering the
 * full set of fields a brand owner may change. Deliberately separate from the
 * super-admin `BrandEditor` (which writes Firestore directly and exposes
 * editorial/curation flags + lookup-creation) — this form goes through the
 * `updateBrandFromPortal` callable, the server-side source of truth for what's
 * editable. We don't refactor BrandEditor.
 *
 * Editorial/curation flags (promoted / disabled / hideImages / isMicroBrand)
 * are deliberately absent — those stay super-admin-only. Lookup *creation* is
 * also withheld (the parentOrg + windingDirections selectors run with
 * `allowCustom={false}`, surfacing a disabled "Request new value →" affordance
 * for a future request flow rather than letting owners mint lookup entries).
 *
 * Renders the form body only; the caller wraps it in a Modal. Initial values
 * come from the brand doc the parent already loaded (`brand` prop); on success
 * we toast and hand the saved fields back via `onSaved` so the parent can
 * refresh its view.
 */

interface EditMyBrandPanelProps {
  brandId: string;
  brand: WatchBrand;
  onSaved: (updates: BrandPortalEditableFields) => void;
  onClose: () => void;
}

export function EditMyBrandPanel({
  brandId,
  brand,
  onSaved,
  onClose,
}: EditMyBrandPanelProps) {
  // Identity
  const [displayName, setDisplayName] = useState(brand.displayName ?? '');
  const [country, setCountry] = useState(brand.country ?? '');
  const [founded, setFounded] = useState(
    brand.founded != null ? String(brand.founded) : '',
  );
  const [website, setWebsite] = useState(
    brand.website ?? brand.mainWebsite ?? '',
  );
  const [mainAddress, setMainAddress] = useState(brand.mainAddress ?? '');

  // Branding / images
  const [logo, setLogo] = useState(brand.logo ?? '');
  const [logoSourceUrl, setLogoSourceUrl] = useState(brand.logoSourceUrl ?? '');
  const [heroImage, setHeroImage] = useState(brand.heroImage ?? '');
  const [heroImageSourceUrl, setHeroImageSourceUrl] = useState(
    brand.heroImageSourceUrl ?? '',
  );
  const [useHeroImage, setUseHeroImage] = useState(brand.useHeroImage === true);

  // Relationships
  const [parentOrg, setParentOrg] = useState(brand.parentOrg ?? '');

  // Names & search
  const [alternativeNames, setAlternativeNames] = useState<string[]>(
    brand.alternativeNames ?? [],
  );
  const [abbreviations, setAbbreviations] = useState<string[]>(
    brand.abbreviations ?? [],
  );
  const [windingDirections, setWindingDirections] = useState<string[]>(
    brand.windingDirections ?? [],
  );

  // Description
  const [description, setDescription] = useState(brand.description ?? '');
  const [information, setInformation] = useState(brand.information ?? '');
  // When on, shoppers see the brand-authored `description` instead of the AI
  // one. Defaults to false/missing (the AI description wins).
  const [useAuthoredDescription, setUseAuthoredDescription] = useState(
    brand.useAuthoredDescription === true,
  );

  // Settings
  // Opt-in: a missing value is OFF, as it is in the app. Reading it as on
  // would show the brand a ticked box for something that is not happening —
  // and saving the form would then switch it on without anyone choosing to.
  const [showProductLinks, setShowProductLinks] = useState(
    brandShowsProductLinks(brand.showProductLinks),
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Entity-request modal (Phase 2.5) ──────────────────────────────────────
  const [requestModal, setRequestModal] = useState<{
    collection: string;
    sourceField: string;
  } | null>(null);
  const [sentRequestFields, setSentRequestFields] = useState<Set<string>>(
    () => new Set(),
  );

  const foundedTrimmed = founded.trim();
  const foundedNum = foundedTrimmed === '' ? null : Number(foundedTrimmed);
  const foundedInvalid =
    foundedTrimmed !== '' &&
    (!Number.isInteger(foundedNum) ||
      (foundedNum as number) < 1700 ||
      (foundedNum as number) > 2200);

  const websiteTrimmed = website.trim();
  const websiteInvalid =
    websiteTrimmed !== '' && !/^https?:\/\/.+/i.test(websiteTrimmed);

  const hasFieldError = foundedInvalid || websiteInvalid;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving || hasFieldError) return;

    const updates: BrandPortalEditableFields = {
      displayName: displayName.trim(),
      country: country.trim(),
      founded: foundedNum,
      // Form-state name stays `website`; the callable accepts it as an alias
      // for the canonical `mainWebsite`.
      website: websiteTrimmed,
      mainAddress,
      logo: logo.trim(),
      logoSourceUrl: logoSourceUrl.trim(),
      heroImage,
      heroImageSourceUrl: heroImageSourceUrl.trim(),
      useHeroImage,
      parentOrg: parentOrg.trim(),
      alternativeNames,
      abbreviations,
      windingDirections,
      description,
      information,
      useAuthoredDescription,
      showProductLinks,
    };

    setSaving(true);
    setError(null);
    try {
      const result = await updateBrandFromPortal(brandId, updates);
      toast.success(
        result.fieldsUpdated.length > 0
          ? `Saved (${result.fieldsUpdated.length} field${
              result.fieldsUpdated.length === 1 ? '' : 's'
            } updated)`
          : 'Saved',
      );
      onSaved(updates);
      onClose();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to save changes';
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500';
  const errorInputClass =
    'w-full border border-red-300 bg-red-50 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500';
  const fieldsetClass = 'space-y-4 rounded-lg border border-gray-200 p-4';
  const legendClass = 'px-1 text-sm font-semibold text-gray-900';

  return (
    <>
    <form onSubmit={handleSubmit} className='space-y-5'>
      <p className='text-sm text-gray-500'>
        Update your brand's public details. Changes appear across Watch Keeper®.
      </p>

      {/* ── Identity ──────────────────────────────────────────────── */}
      <fieldset className={fieldsetClass}>
        <legend className={legendClass}>Identity</legend>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Display name
          </label>
          <input
            type='text'
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={brand.name}
            className={inputClass}
          />
          <p className='mt-1 text-xs text-gray-400'>
            The name shown to users. Your brand's canonical id ({brand.name}) is
            set by Watch Keeper and can't be changed here.
          </p>
        </div>

        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
          <CountrySelect value={country} onChange={setCountry} label='Country' />

          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              Founded (year)
            </label>
            <input
              type='number'
              value={founded}
              onChange={(e) => setFounded(e.target.value)}
              placeholder='e.g. 1905'
              className={foundedInvalid ? errorInputClass : inputClass}
            />
            {foundedInvalid && (
              <p className='mt-1 text-xs text-red-600'>
                Enter a 4-digit year between 1700 and 2200.
              </p>
            )}
          </div>
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Website
          </label>
          <input
            type='url'
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder='https://example.com'
            className={websiteInvalid ? errorInputClass : inputClass}
          />
          {websiteInvalid && (
            <p className='mt-1 text-xs text-red-600'>
              Must start with http:// or https:// (or leave empty).
            </p>
          )}
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Address
          </label>
          <textarea
            value={mainAddress}
            onChange={(e) => setMainAddress(e.target.value)}
            rows={3}
            placeholder='Company address…'
            className={inputClass}
          />
        </div>
      </fieldset>

      {/* ── Branding / Images ─────────────────────────────────────── */}
      <fieldset className={fieldsetClass}>
        <legend className={legendClass}>Branding / Images</legend>

        <BrandImageUpload
          brandId={brandId}
          variant='logo'
          imageUrl={logo}
          sourceUrl={logoSourceUrl}
          onImageUrlChange={setLogo}
          onSourceUrlChange={setLogoSourceUrl}
        />

        <BrandImageUpload
          brandId={brandId}
          variant='hero'
          imageUrl={heroImage}
          sourceUrl={heroImageSourceUrl}
          showHeroToggle
          useHeroImage={useHeroImage}
          onImageUrlChange={setHeroImage}
          onSourceUrlChange={setHeroImageSourceUrl}
          onUseHeroImageChange={setUseHeroImage}
        />
      </fieldset>

      {/* ── Relationships ─────────────────────────────────────────── */}
      <fieldset className={fieldsetClass}>
        <legend className={legendClass}>Relationships</legend>

        <LookupFieldSelect
          label='Parent organisation'
          value={parentOrg}
          lookupCollection='lookup_parent_organization'
          onChange={setParentOrg}
          allowCustom={false}
          placeholder='Select a parent organisation…'
          onRequestNewValue={() =>
            setRequestModal({
              collection: 'lookup_parent_organization',
              sourceField: 'parentOrg',
            })
          }
          requestSent={sentRequestFields.has('parentOrg')}
        />
      </fieldset>

      {/* ── Names & Search ────────────────────────────────────────── */}
      <fieldset className={fieldsetClass}>
        <legend className={legendClass}>Names &amp; Search</legend>

        <div>
          <TagInput
            label='Also known as'
            values={alternativeNames}
            onChange={setAlternativeNames}
            placeholder='Add an alternative name…'
          />
          <p className='mt-1 text-xs text-gray-400'>
            Alternative names or spellings users might search for.
          </p>
        </div>

        <div>
          <TagInput
            label='Abbreviations'
            values={abbreviations}
            onChange={setAbbreviations}
            placeholder='Add an abbreviation…'
            transform={(v) => v.toUpperCase()}
          />
          <p className='mt-1 text-xs text-gray-400'>
            Short codes for your brand (entered in upper case).
          </p>
        </div>

        <LookupMultiSelect
          label='Winding directions'
          values={windingDirections}
          lookupCollection='lookup_winding_directions'
          onChange={setWindingDirections}
          allowCustom={false}
          onRequestNewValue={() =>
            setRequestModal({
              collection: 'lookup_winding_directions',
              sourceField: 'windingDirections',
            })
          }
          requestSent={sentRequestFields.has('windingDirections')}
        />
      </fieldset>

      {/* ── Description ── (existing AI block from phase ii) ───────── */}
      <fieldset className={fieldsetClass}>
        <legend className={legendClass}>Description</legend>

        {/* AI-generated description (read-only) */}
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            AI-generated description
          </label>
          <div className='w-full min-h-[6rem] whitespace-pre-line rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-500'>
            {brand.aiDescription?.trim() || 'No AI description yet'}
          </div>
          <p className='mt-1 text-xs text-gray-400'>
            Generated automatically. Contact support to regenerate.
          </p>
        </div>

        {/* Toggle: use the brand-authored description instead */}
        <div className='flex items-start gap-3'>
          <input
            id='useAuthoredDescription'
            type='checkbox'
            checked={useAuthoredDescription}
            onChange={(e) => setUseAuthoredDescription(e.target.checked)}
            className='mt-1 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500'
          />
          <label htmlFor='useAuthoredDescription' className='text-sm'>
            <span className='font-medium text-gray-700'>
              Use my own description instead
            </span>
            <span className='block text-xs text-gray-400'>
              When on, shoppers see your description below instead of the AI one.
            </span>
          </label>
        </div>

        {/* Brand-authored description (editable) */}
        <div className={useAuthoredDescription ? undefined : 'opacity-60'}>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Your description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={5}
            className={inputClass}
          />
        </div>

        {/* Additional information */}
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Information
          </label>
          <textarea
            value={information}
            onChange={(e) => setInformation(e.target.value)}
            rows={4}
            className={inputClass}
          />
          <p className='mt-1 text-xs text-gray-400'>
            Extra details shown alongside your brand (history, notes, etc.).
          </p>
        </div>
      </fieldset>

      {/* ── Settings ──────────────────────────────────────────────── */}
      <fieldset className={fieldsetClass}>
        <legend className={legendClass}>Settings</legend>

        <div className='flex items-start gap-3'>
          <input
            id='showProductLinks'
            type='checkbox'
            checked={showProductLinks}
            onChange={(e) => setShowProductLinks(e.target.checked)}
            className='mt-1 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500'
          />
          <label htmlFor='showProductLinks' className='text-sm'>
            <span className='font-medium text-gray-700'>
              Show "View product page" links
            </span>
            <span className='block text-xs text-gray-400'>
              Let users link out to product pages for your references. (Also
              subject to a global Watch Keeper setting.)
            </span>
          </label>
        </div>
      </fieldset>

      {error && (
        <div className='rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700'>
          {error}
        </div>
      )}

      <div className='flex items-center justify-end gap-3 pt-2 border-t border-gray-100'>
        <button
          type='button'
          onClick={onClose}
          disabled={saving}
          className='px-4 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50'
        >
          Cancel
        </button>
        <button
          type='submit'
          disabled={saving || hasFieldError}
          className='px-4 py-2 text-sm bg-brand-600 text-white rounded hover:bg-brand-700 disabled:opacity-50'
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </form>

    {requestModal && (
      <RequestNewValueModal
        visible
        brandId={brandId}
        type='lookup'
        collection={requestModal.collection}
        context={{ sourceField: requestModal.sourceField }}
        onClose={() => setRequestModal(null)}
        onSubmitted={() => {
          const field = requestModal.sourceField;
          setSentRequestFields((prev) => new Set(prev).add(field));
        }}
      />
    )}
    </>
  );
}
